"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  User,
  Mail,
  Calendar,
  Vote,
  Edit2,
  Save,
  X,
  Shield,
  Target,
  Activity,
  AlertCircle,
  HelpCircle,
  BarChart3,
} from "lucide-react";
import {
  formatDate,
  cn,
  STAV_ZDRAVIA_LABELS,
  STAV_ZDRAVIA_COLORS,
} from "@/lib/utils";
import {
  Profil,
  Hlasovanie,
  VyberSKandidatom,
  PoziciaHraca,
  stav_zdravia,
  HracKomplet,
} from "@/types/database";

import { toast } from "sonner";

const TITLE_CLASSES = cn(
  "text-3xl md:text-5xl font-black tracking-tighter uppercase text-foreground drop-shadow-sm"
);
const INFO_ROW_CLASSES =
  "flex items-center gap-3 p-4 bg-muted/10 border border-muted/10 rounded-xl transition-all hover:bg-muted/20";

export default function ProfilPage() {
  const PAGE_TITLE = "Môj profil";
  const SUBTITLE_TEXT = "Spravujte svoje údaje a prezrite si hlasovania";
  const BASIC_INFO_TITLE = "Základné informácie";
  const BASIC_INFO_DESC = "Vaše osobné údaje a nastavenia profilu";
  const ACTIVE_VOTING_TITLE = "Aktuálne hlasovanie";
  const QUICK_ACTIONS_TITLE = "Rýchle akcie";
  const QUICK_ACTIONS_DESC = "Prejdite na ostatné časti aplikácie";

  const supabase = createClient();
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [profil, setProfil] = useState<Profil | null>(null);
  const [aktualneHlasovanie, setAktualneHlasovanie] =
    useState<Hlasovanie | null>(null);
  const [mojHlas, setMojHlas] = useState<VyberSKandidatom[]>([]);
  const [editujeProfil, setEditujeProfil] = useState(false);
  const [zobrazovane_meno, setZobrazovaneMeno] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const nacitatData = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        window.location.href = "/prihlasenie?redirect=/profil";
        return;
      }

      setUser({ id: user.id, email: user.email || "" });

      // Načítanie profilu
      const { data: profilData } = await supabase
        .from("profil")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profilData) {
        const profilWithAvatar: Profil = {
          id: profilData.id,
          email: profilData.email,
          zobrazovane_meno: profilData.zobrazovane_meno,
          rola: profilData.rola,
          vytvorene: profilData.vytvorene,
          avatar_url: profilData.avatar_url || null,
          avatar_verzia: profilData.avatar_verzia || 0,
        };
        setProfil(profilWithAvatar);
        setZobrazovaneMeno(profilData.zobrazovane_meno || "");
      }

      // Načítanie aktívneho hlasovania
      const { data: hlasovanieData } = await supabase
        .from("hlasovanie")
        .select("*")
        .eq("je_aktivne", true)
        .single();

      if (hlasovanieData) {
        setAktualneHlasovanie(hlasovanieData);

        // Načítanie hlasu používateľa
        const { data: hlasData } = await supabase
          .from("hlas")
          .select("id")
          .eq("hlasovanie_id", hlasovanieData.id)
          .eq("profil_id", user.id)
          .single();

        if (hlasData) {
          // Načítanie výberu s kandidátmi
          const { data: vyberyRaw } = await supabase
            .from("vyber")
            .select(
              `
              *,
              kandidat:kandidat(
                *,
                hrac:hrac(*)
              )
            `
            )
            .eq("hlas_id", hlasData.id);

          if (vyberyRaw) {
            const vybery: VyberSKandidatom[] = (vyberyRaw || [])
              .map((v) => {
                const k = v.kandidat;
                if (!k || typeof k !== "object") return null;

                // We know the structure from the query
                const kandidatData = k as unknown as {
                  id: string;
                  hlasovanie_id: string;
                  hrac_id: string;
                  je_v_dispozicii: boolean;
                  stav_zdravia: stav_zdravia;
                  poznamka_zdravie: string | null;
                  je_zobrazovat: boolean;
                  hrac: HracKomplet;
                };

                return {
                  id: v.id,
                  hlas_id: v.hlas_id,
                  pozicia: v.pozicia,
                  slot: v.slot,
                  kandidat_id: v.kandidat_id,
                  kandidat: {
                    id: kandidatData.id,
                    hlasovanie_id: kandidatData.hlasovanie_id,
                    hrac_id: kandidatData.hrac_id,
                    je_v_dispozicii: kandidatData.je_v_dispozicii,
                    stav_zdravia: kandidatData.stav_zdravia,
                    poznamka_zdravie: kandidatData.poznamka_zdravie,
                    je_zobrazovat: kandidatData.je_zobrazovat,
                    hrac: kandidatData.hrac,
                  },
                };
              })
              .filter((v): v is VyberSKandidatom => v !== null);

            setMojHlas(vybery);
          }
        }
      }
    };

    nacitatData();
  }, [supabase]);

  const handleUlozitProfil = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const odpoved = await supabase
        .from("profil")
        .update({ zobrazovane_meno: zobrazovane_meno.trim() || null })
        .eq("id", user.id);

      if (odpoved.error) throw odpoved.error;

      setProfil((prev) =>
        prev
          ? { ...prev, zobrazovane_meno: zobrazovane_meno.trim() || null }
          : null
      );
      setEditujeProfil(false);
      toast.success("Profil aktualizovaný");
    } catch {
      toast.error("Chyba pri aktualizácii profilu");
    } finally {
      setLoading(false);
    }
  };

  const getPoziciaIcon = (pozicia: PoziciaHraca) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-4 w-4" />;
      case "obranca":
        return <User className="h-4 w-4" />;
      case "utocnik":
        return <Target className="h-4 w-4" />;
    }
  };

  const getPoziciaLabel = (pozicia: PoziciaHraca) => {
    switch (pozicia) {
      case "brankar":
        return "Brankár";
      case "obranca":
        return "Obranca";
      case "utocnik":
        return "Útočník";
    }
  };

  const StavZdraviaIcon = ({ stav }: { stav: stav_zdravia }) => {
    switch (stav) {
      case "fit":
        return <Activity className="h-3 w-3" />;
      case "zraneny":
        return <AlertCircle className="h-3 w-3" />;
      case "otaznik":
        return <HelpCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getSeradeneVybery = () => {
    const seradenie: { [key in PoziciaHraca]: VyberSKandidatom[] } = {
      brankar: [],
      obranca: [],
      utocnik: [],
    };

    mojHlas.forEach((vyber) => {
      seradenie[vyber.pozicia].push(vyber);
    });

    Object.keys(seradenie).forEach((key) => {
      seradenie[key as PoziciaHraca].sort((a, b) => a.slot - b.slot);
    });

    return seradenie;
  };

  if (!user || !profil) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Načítavam profil...</p>
        </div>
      </div>
    );
  }

  const seradeneVybery = getSeradeneVybery();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Hlavička profilu */}
        <div className="mb-12 space-y-2 border-b border-muted/20 pb-8">
          <h1 className={TITLE_CLASSES}>{PAGE_TITLE}</h1>
          <p className="text-muted-foreground/80 md:text-lg font-medium">
            {SUBTITLE_TEXT}
          </p>
        </div>

        {/* Základné informácie */}
        <Card className="mb-12 border-muted/20 bg-transparent shadow-sm rounded-3xl overflow-hidden">
          <CardHeader className="bg-muted/5 border-b border-muted/10 p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <UserAvatar
                  userId={user.id}
                  meno={profil.zobrazovane_meno || undefined}
                  email={user.email}
                  avatarUrl={profil.avatar_url}
                  avatarVerzia={profil.avatar_verzia}
                  className="h-16 w-16"
                  fallbackClassName="text-2xl"
                />
                <div className="space-y-1">
                  <CardTitle className="text-xl md:text-2xl font-black uppercase tracking-tight">
                    {BASIC_INFO_TITLE}
                  </CardTitle>
                  <CardDescription className="font-medium text-muted-foreground">
                    {BASIC_INFO_DESC}
                  </CardDescription>
                </div>
              </div>
              {profil.rola === "admin" && (
                <Badge
                  variant="default"
                  className="bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] px-4 py-1 rounded-full shadow-sm"
                >
                  Admin
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-10 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60 ml-1">
                  Email
                </Label>
                <div className={INFO_ROW_CLASSES}>
                  <Mail className="h-4 w-4 text-primary/60" />
                  <span className="text-foreground font-bold">
                    {user.email}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60 ml-1">
                  Zobrazované meno
                </Label>
                {editujeProfil ? (
                  <div className="flex flex-col gap-3">
                    <Input
                      value={zobrazovane_meno}
                      onChange={(e) => setZobrazovaneMeno(e.target.value)}
                      placeholder="Zadajte meno"
                      className="h-14 text-lg font-bold border-muted/20 bg-background/50 focus:border-primary/50 transition-all rounded-xl px-4"
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleUlozitProfil}
                        disabled={loading}
                        className="flex-1 rounded-full font-black uppercase tracking-widest text-xs h-12 shadow-lg"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Uložiť
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditujeProfil(false);
                          setZobrazovaneMeno(profil.zobrazovane_meno || "");
                        }}
                        className="flex-1 rounded-full font-black uppercase tracking-widest text-xs h-12"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Zrušiť
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-muted/5 border border-muted/10 rounded-xl transition-all hover:bg-muted/10 group">
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        userId={user.id}
                        meno={profil.zobrazovane_meno || undefined}
                        email={user.email}
                        avatarUrl={profil.avatar_url}
                        avatarVerzia={profil.avatar_verzia}
                        className="h-16 w-16"
                        fallbackClassName="text-2xl"
                      />
                      <span className="text-foreground font-black text-lg">
                        {profil.zobrazovane_meno || "—"}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full hover:bg-primary/10 hover:text-primary transition-all p-2 h-9 w-9"
                      onClick={() => setEditujeProfil(true)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-muted/10">
              <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60 ml-1">
                Člen od
              </Label>
              <div className="flex items-center gap-3 text-muted-foreground/80 font-bold uppercase tracking-widest text-xs px-1">
                <Calendar className="h-4 w-4 text-primary/40" />
                <span>{formatDate(profil.vytvorene)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aktuálne hlasovanie */}
        {aktualneHlasovanie && (
          <Card className="mb-12 border-muted/20 bg-transparent shadow-sm rounded-3xl overflow-hidden">
            <CardHeader className="bg-muted/5 border-b border-muted/10 p-6 md:p-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-status-success/10 rounded-2xl text-status-success shadow-inner">
                  <Vote className="h-7 w-7" />
                </div>
                <div className="space-y-1">
                  <CardTitle className="text-xl md:text-2xl font-black uppercase tracking-tight">
                    {ACTIVE_VOTING_TITLE}
                  </CardTitle>
                  <CardDescription className="font-bold text-status-success/80 uppercase tracking-widest text-xs">
                    {aktualneHlasovanie.nazov}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 md:p-10">
              {mojHlas.length > 0 ? (
                <div className="space-y-10">
                  <p className="text-sm font-black uppercase tracking-widest text-muted-foreground/60 border-b border-muted/10 pb-4">
                    Vaša zostava:
                  </p>

                  {Object.entries(seradeneVybery).map(
                    ([pozicia, vybery]) =>
                      vybery.length > 0 && (
                        <div key={pozicia} className="space-y-6">
                          <div className="flex items-center gap-3">
                            <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
                              {getPoziciaIcon(pozicia as PoziciaHraca)}
                            </div>
                            <h3 className="font-black text-xl uppercase tracking-tight">
                              {getPoziciaLabel(pozicia as PoziciaHraca)}
                            </h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {vybery.map((vyber) => (
                              <Card
                                key={vyber.id}
                                className="border-muted/10 bg-muted/5 shadow-none rounded-2xl hover:bg-muted/10 transition-colors"
                              >
                                <CardContent className="p-5">
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <Badge
                                        variant="outline"
                                        className="text-[9px] font-black uppercase tracking-widest bg-background/50 border-muted/20 rounded-md"
                                      >
                                        Slot {vyber.slot}
                                      </Badge>
                                    </div>
                                    <div className="font-black text-base text-foreground tracking-tight leading-tight">
                                      {vyber.kandidat.hrac.cele_meno}
                                    </div>
                                    <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider truncate">
                                      {vyber.kandidat.hrac.klub || "Bez klubu"}
                                      {vyber.kandidat.hrac.sutaz &&
                                        ` • ${vyber.kandidat.hrac.sutaz}`}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                      {!vyber.kandidat.je_v_dispozicii && (
                                        <Badge
                                          variant="destructive"
                                          className="text-[9px] font-black uppercase tracking-widest rounded-full px-2"
                                        >
                                          Nedostupný
                                        </Badge>
                                      )}
                                      {vyber.kandidat.stav_zdravia !==
                                        "nezname" && (
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "text-[9px] font-black uppercase tracking-widest rounded-full px-2 flex items-center gap-1.5",
                                            STAV_ZDRAVIA_COLORS[
                                              vyber.kandidat.stav_zdravia
                                            ]
                                          )}
                                        >
                                          <StavZdraviaIcon
                                            stav={vyber.kandidat.stav_zdravia}
                                          />
                                          <span>
                                            {
                                              STAV_ZDRAVIA_LABELS[
                                                vyber.kandidat.stav_zdravia
                                              ]
                                            }
                                          </span>
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-muted/10">
                    <Button
                      asChild
                      size="lg"
                      className="flex-1 rounded-full font-black uppercase tracking-widest text-xs h-14 shadow-lg hover:shadow-xl transition-all"
                    >
                      <Link href="/">Upraviť hlas</Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="flex-1 rounded-full font-black uppercase tracking-widest text-xs h-14 shadow-sm border-muted/20"
                    >
                      <Link href={`/vysledky/${aktualneHlasovanie.id}`}>
                        Zobraziť výsledky
                      </Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 space-y-6">
                  <div className="p-6 bg-muted/50 rounded-full w-fit mx-auto shadow-inner">
                    <Vote className="h-12 w-12 text-muted-foreground/20" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-black uppercase tracking-tight">
                      Ešte ste nehlasovali
                    </h3>
                    <p className="text-muted-foreground/80 font-medium max-w-sm mx-auto">
                      Zapojte sa do hlasovania a vytvorte si vlastnú olympijskú
                      nomináciu.
                    </p>
                  </div>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="rounded-full px-10 h-14 font-black uppercase tracking-widest text-xs border-muted/20 shadow-sm"
                  >
                    <Link href="/" className="flex items-center gap-3">
                      <Vote className="h-5 w-5 text-primary" />
                      <span>Hlasovať teraz</span>
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rýchle akcie */}
        <Card className="border-muted/20 bg-transparent shadow-sm rounded-3xl overflow-hidden mb-12">
          <CardHeader className="bg-muted/5 border-b border-muted/10 p-6 md:p-8">
            <div className="space-y-1">
              <CardTitle className="text-xl md:text-2xl font-black uppercase tracking-tight">
                {QUICK_ACTIONS_TITLE}
              </CardTitle>
              <CardDescription className="font-medium text-muted-foreground">
                {QUICK_ACTIONS_DESC}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                asChild
                variant="outline"
                className="h-auto p-6 rounded-2xl flex-col gap-4 border-muted/20 hover:bg-primary/5 hover:border-primary/30 transition-all group"
              >
                <Link
                  href="/"
                  className="flex flex-col items-center gap-3 w-full"
                >
                  <div className="p-3 bg-primary/5 rounded-xl text-primary group-hover:scale-110 transition-transform shadow-inner">
                    <Vote className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-black uppercase tracking-widest">
                    Domov
                  </span>
                </Link>
              </Button>

              {aktualneHlasovanie && (
                <Button
                  asChild
                  variant="outline"
                  className="h-auto p-6 rounded-2xl flex-col gap-4 border-muted/20 hover:bg-primary/5 hover:border-primary/30 transition-all group"
                >
                  <Link
                    href={`/vysledky/${aktualneHlasovanie.id}`}
                    className="flex flex-col items-center gap-3 w-full"
                  >
                    <div className="p-3 bg-primary/5 rounded-xl text-primary group-hover:scale-110 transition-transform shadow-inner">
                      <BarChart3 className="h-6 w-6" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest">
                      Výsledky
                    </span>
                  </Link>
                </Button>
              )}

              {profil.rola === "admin" && (
                <Button
                  asChild
                  variant="outline"
                  className="h-auto p-6 rounded-2xl flex-col gap-4 border-muted/20 hover:bg-primary/5 hover:border-primary/30 transition-all group"
                >
                  <Link
                    href="/admin"
                    className="flex flex-col items-center gap-3 w-full"
                  >
                    <div className="p-3 bg-primary/5 rounded-xl text-primary group-hover:scale-110 transition-transform shadow-inner">
                      <Shield className="h-6 w-6" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest">
                      Admin panel
                    </span>
                  </Link>
                </Button>
              )}

              <Button
                variant="outline"
                className="h-auto p-6 rounded-2xl flex-col gap-4 border-muted/20 hover:bg-destructive/5 hover:border-destructive/30 transition-all group"
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = "/";
                }}
              >
                <div className="flex flex-col items-center gap-3 w-full">
                  <div className="p-3 bg-destructive/5 rounded-xl text-destructive group-hover:scale-110 transition-transform shadow-inner">
                    <X className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-black uppercase tracking-widest text-destructive">
                    LogOut
                  </span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
